import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { astroService } from "@/services/astro";
import { Loader2, Settings, GitBranch, Github } from "lucide-react";
import { AstroIntegration } from "@/types/integration";

interface AstroSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
  integration: AstroIntegration;
}

export function AstroSettingsDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
  integration,
}: AstroSettingsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    defaultAuthor: integration.settings?.defaultAuthor || "",
    defaultTags: integration.settings?.defaultTags?.join(", ") || "",
    commitMessageTemplate:
      integration.settings?.commitMessageTemplate ||
      "Publish article: {{.Title}}",
    autoPublish: integration.settings?.autoPublish ?? false,
    createPullRequest: integration.settings?.createPullRequest ?? true,
  });

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await astroService.updateSettings({
        brandId,
        defaultAuthor: settings.defaultAuthor,
        defaultTags: settings.defaultTags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        commitMessageTemplate: settings.commitMessageTemplate,
        autoPublish: settings.autoPublish,
        createPullRequest: settings.createPullRequest,
      });

      toast.success("Astro settings updated successfully!");
      onOpenChange(false);
      onSuccess();
    } catch (error: unknown) {
      console.error("Error updating Astro settings:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to update settings",
      );
    } finally {
      setLoading(false);
    }
  };

  const parseRepositoryInfo = () => {
    const url = integration.credentials.repositoryUrl;
    const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (match) {
      return {
        owner: match[1],
        repo: match[2].replace(".git", ""),
      };
    }
    return { owner: "Unknown", repo: "Unknown" };
  };

  const { owner, repo } = parseRepositoryInfo();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Astro Integration Settings
          </DialogTitle>
          <DialogDescription>
            Configure your Astro static site publishing preferences and defaults
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Current Repository Info */}
          <div className="rounded-lg border p-4 bg-slate-50">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <Github className="h-4 w-4" />
              Connected Repository
            </h4>
            <div className="text-sm space-y-1">
              <p>
                <strong>Repository:</strong> {owner}/{repo}
              </p>
              <p className="flex items-center gap-1">
                <GitBranch className="h-3 w-3" />
                <strong>Branch:</strong> {integration.credentials.branch}
              </p>
              <p>
                <strong>Content Path:</strong>{" "}
                {integration.credentials.contentPath}
              </p>
            </div>
          </div>

          {/* Publishing Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Publishing Settings</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-publish">Auto-publish articles</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically create GitHub commits and PRs when articles are
                  created
                </p>
              </div>
              <Switch
                id="auto-publish"
                checked={settings.autoPublish}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({ ...prev, autoPublish: checked }))
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="create-pr">Create Pull Requests</Label>
                <p className="text-sm text-muted-foreground">
                  Create pull requests instead of committing directly to the
                  main branch
                </p>
              </div>
              <Switch
                id="create-pr"
                checked={settings.createPullRequest}
                onCheckedChange={(checked) =>
                  setSettings((prev) => ({
                    ...prev,
                    createPullRequest: checked,
                  }))
                }
              />
            </div>
          </div>

          {/* Content Defaults */}
          <div className="space-y-4">
            <h4 className="font-medium">Content Defaults</h4>

            <div className="space-y-2">
              <Label htmlFor="default-author">Default Author</Label>
              <Input
                id="default-author"
                placeholder="e.g., Creator Team"
                value={settings.defaultAuthor}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    defaultAuthor: e.target.value,
                  }))
                }
              />
              <p className="text-sm text-muted-foreground">
                Author name for articles (can be overridden per article)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="default-tags">Default Tags</Label>
              <Input
                id="default-tags"
                placeholder="e.g., blog, astro, static-site"
                value={settings.defaultTags}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    defaultTags: e.target.value,
                  }))
                }
              />
              <p className="text-sm text-muted-foreground">
                Comma-separated list of default tags for articles
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="commit-template">Commit Message Template</Label>
              <Textarea
                id="commit-template"
                placeholder="Publish article: {{.Title}}"
                value={settings.commitMessageTemplate}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    commitMessageTemplate: e.target.value,
                  }))
                }
                rows={2}
              />
              <p className="text-sm text-muted-foreground">
                Template for Git commit messages. Use {"{{"}.Title{"}"} for
                article title
              </p>
            </div>
          </div>

          {/* Current Settings Preview */}
          <div className="rounded-lg border p-4 bg-blue-50">
            <h4 className="font-medium mb-2">Current Settings</h4>
            <div className="text-sm space-y-1">
              <p>
                <strong>Auto-publish:</strong>{" "}
                {settings.autoPublish ? "Enabled" : "Disabled"}
              </p>
              <p>
                <strong>Create PRs:</strong>{" "}
                {settings.createPullRequest ? "Yes" : "No (direct commits)"}
              </p>
              {settings.defaultAuthor && (
                <p>
                  <strong>Default Author:</strong> {settings.defaultAuthor}
                </p>
              )}
              {settings.defaultTags && (
                <p>
                  <strong>Default Tags:</strong> {settings.defaultTags}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 pt-4 border-t bg-white sticky bottom-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveSettings} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Settings"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AstroSettingsButton({
  brandId,
  integration,
  onSuccess,
}: {
  brandId: string;
  integration: AstroIntegration;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="ml-2"
      >
        <Settings className="mr-1 h-3 w-3" />
        Settings
      </Button>

      <AstroSettingsDialog
        open={open}
        onOpenChange={setOpen}
        onSuccess={onSuccess}
        brandId={brandId}
        integration={integration}
      />
    </>
  );
}
